import {
  MessageTemplatesService,
  TemplatePreview,
  TemplatePreviewRequest,
} from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import { useEffect, useRef, useState } from "react";
import { segmentWords } from "./templatesApi";

/**
 * The wording filled in for one person, from the server (which fills it in
 * the same way it sends it), about `delay` ms after the request last
 * changed. Nothing is sent. An answer to an older request is dropped. A
 * new `refresh` asks again for the same request (the brand changed).
 */
export function useTemplatePreview(
  orgId: number,
  request: TemplatePreviewRequest | null,
  delay = 400,
  refresh = 0
) {
  const [preview, setPreview] = useState<TemplatePreview>();
  const [error, setError] = useState<string>();
  const key = request ? JSON.stringify(request) : null;
  const asked = useRef(0);

  useEffect(() => {
    if (key === null) return;
    const ask = ++asked.current;
    const timer = setTimeout(() => {
      MessageTemplatesService.previewMessageTemplate({
        orgId,
        requestBody: JSON.parse(key),
      })
        .then((answer) => {
          if (ask !== asked.current) return;
          setPreview(answer);
          setError(undefined);
        })
        .catch((e) => {
          if (ask !== asked.current) return;
          setError(errorText(e, "The preview could not be loaded."));
        });
    }, delay);
    return () => clearTimeout(timer);
  }, [orgId, key, delay, refresh]);

  return { preview, error };
}

/**
 * The email as it arrives. Sandboxed: nothing in it runs, and its links
 * don't open in the app.
 */
export function EmailFrame({
  html,
  title,
  height = 460,
}: {
  html: string;
  title: string;
  height?: number;
}) {
  return (
    <iframe
      title={title}
      sandbox=""
      srcDoc={html}
      style={{ height }}
      className="w-full rounded-lg border border-gray-200 bg-white"
    />
  );
}

/** The text as it shows on a phone. */
export function TextBubble({ text }: { text: string }) {
  return (
    <div className="rounded-2xl bg-gray-100 p-4">
      {text ? (
        <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-sm bg-white px-4 py-2 text-sm text-gray-900 shadow-sm">
          {text}
        </p>
      ) : (
        <p className="text-sm text-gray-500">This template has no text yet.</p>
      )}
    </div>
  );
}

/** How long the text is, and why a Unicode text fits fewer characters. */
export function SegmentCount({ preview }: { preview: TemplatePreview }) {
  if (!preview.sms) return null;
  return (
    <p className="mt-1 text-xs text-gray-500">
      {segmentWords(preview)}.{" "}
      {preview.sms_encoding === TemplatePreview.sms_encoding.UNICODE
        ? "Emoji and some symbols make it a Unicode text, which fits 70 characters in one text instead of 160."
        : "Up to 160 characters fit in one text."}
    </p>
  );
}

/** "A", "A and B", or "A, B, and C". */
export function namesInWords(names: string[]) {
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/**
 * Who unsubscribed from a promotional email, in words: their names when
 * there are a few, else how many.
 */
export function unsubscribedWords(count: number, names: string[] = []) {
  const who =
    names.length > 0 && names.length <= 5
      ? namesInWords(names)
      : count === 1
      ? "1 person"
      : `${count} people`;
  return `${who} unsubscribed from promotional emails, so they don't get the email.`;
}

/**
 * What was taken out of your own HTML, an email Gmail would cut off, the
 * details that could not be filled in (for the person shown, and for the
 * others it is for), what looks like a variable but isn't one, and who
 * unsubscribed from promotional emails.
 */
export function PreviewNotes({ preview }: { preview: TemplatePreview }) {
  const name = preview.person?.name || "this person";
  const others = preview.others_missing ?? [];
  const unsubscribed = preview.unsubscribed ?? [];
  // "This form: Their own link to the form" fills in only when an
  // automation sends about a form or meeting; the rest is the person's own
  // details.
  const fromTrigger = preview.missing.filter((label) =>
    /^This (form|meeting)\b/.test(label)
  );
  const onFile = preview.missing
    .filter((label) => !fromTrigger.includes(label))
    .map((label) => {
      const words = label.includes(": ") ? label.split(": ")[1] : label;
      return words.charAt(0).toLowerCase() + words.slice(1);
    });
  if (
    !preview.removed &&
    !preview.size_note &&
    !preview.missing.length &&
    !preview.unknown.length &&
    !others.length &&
    !unsubscribed.length
  )
    return null;
  return (
    <div className="mt-3 space-y-1 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
      {preview.removed && <p>{preview.removed}</p>}
      {preview.size_note && <p>{preview.size_note}</p>}
      {unsubscribed.length > 0 && (
        <p>{unsubscribedWords(unsubscribed.length, unsubscribed)}</p>
      )}
      {fromTrigger.length > 0 && (
        <p>Form and meeting details fill in when an automation sends this.</p>
      )}
      {onFile.length > 0 && (
        <p>
          {name} has no {onFile.join(", ")} on file, so{" "}
          {onFile.length === 1 ? "it's" : "they're"} left blank.
        </p>
      )}
      {others.slice(0, 5).map((other) => (
        <p key={other.name}>
          For {other.name}: {other.missing.join(", ")}.
        </p>
      ))}
      {others.length > 5 && (
        <p>And {others.length - 5} more people have blank details.</p>
      )}
      {preview.unknown.length > 0 && (
        <p>
          We don&apos;t know these variables:{" "}
          {preview.unknown.map((key) => `{{${key}}}`).join(", ")}. Check their
          spelling.
        </p>
      )}
    </div>
  );
}
